const API_BASE = process.env.REACT_APP_API_BASE;

const TOKEN_KEY = "multipli-admin-token";

export function getToken() {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || null;
  } catch (e) {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch (e) {
  }
}

export class ApiError extends Error {
  constructor(message, code, httpStatus) {
    super(message);
    this.code = code;
    this.httpStatus = httpStatus;
  }
}

const READ_ONLY_METHODS = ["GET", "HEAD"];

let confirmationApplies = false;

export function setConfirmationApplies(applies) {
  confirmationApplies = Boolean(applies);
}

const changesState = (method, path) =>
  confirmationApplies &&
  !READ_ONLY_METHODS.includes(method) &&
  path.startsWith("/admin") &&
  path !== "/admin/confirm";

let confirmationWindow = 0;

export function openConfirmationWindow() {
  confirmationWindow += 1;
}

export function closeConfirmationWindow() {
  confirmationWindow = Math.max(0, confirmationWindow - 1);
}

async function mintConfirmation(method, path, payload) {
  const response = await fetch(`${API_BASE}/admin/confirm`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ method, path, payload }),
  });

  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiError(
      body.message || "That change could not be confirmed.",
      body.code || null,
      response.status
    );
  }

  return body.data.token;
}

export async function apiCall(method, path, body, options = {}) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const payload = body ? JSON.stringify(body) : "";

  if (changesState(method, path)) {
    if (!confirmationWindow) {
      throw new ApiError(
        "This change has to go through a confirmation before it can be made.",
        "confirmation_required",
        null
      );
    }
    headers["x-admin-confirm"] = await mintConfirmation(method, path, payload);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    ...(payload ? { body: payload } : {}),
  });

  const contentType = response.headers.get("content-type") || "";

  if (options.binary) {
    if (!response.ok) {
      const message = await response.text().catch(() => "Request failed");
      throw new ApiError(message, null, response.status);
    }
    return response;
  }

  const parsed = contentType.includes("json")
    ? await response.json().catch(() => ({}))
    : {};

  if (!response.ok) {
    throw new ApiError(
      parsed.message || "Something went wrong.",
      parsed.code || null,
      response.status
    );
  }

  return parsed.data;
}

export const api = {
  get: (path, options) => apiCall("GET", path, null, options),
  post: (path, body, options) => apiCall("POST", path, body, options),
  put: (path, body, options) => apiCall("PUT", path, body, options),
  patch: (path, body, options) => apiCall("PATCH", path, body, options),
  delete: (path, options) => apiCall("DELETE", path, null, options),
};

export { API_BASE };
